import React, { useEffect, useState } from 'react';
import { Rocket, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { dashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format, isToday, isTomorrow } from 'date-fns';

const MANAGER_ROLES = new Set(['admin', 'team_lead', 'delivery_manager']);

const dayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isToday(d)) return 'today';
  if (isTomorrow(d)) return 'tomorrow';
  return format(d, 'EEE');
};

/**
 * "Joining this week" — managers-only mini-strip on the Dashboard.
 *
 * Shows a friendly one-line summary when ≤ 3 joiners ("🚀 3 joining this week:
 * Anita (Mon), Rahul (Wed), Priya (Fri)"); collapsible expanded list with
 * client + recruiter info when > 3. Hidden entirely when nobody is joining
 * to keep the Dashboard clean.
 */
const JoiningThisWeekTile = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!MANAGER_ROLES.has(user?.role)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    dashboardApi
      .getJoiningThisWeek(7)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  // Hide entirely for non-managers, while loading, or when there's nothing to show.
  if (!MANAGER_ROLES.has(user?.role) || loading || !data || data.count === 0) {
    if (loading && MANAGER_ROLES.has(user?.role)) {
      return (
        <div
          className="flex items-center gap-2 text-xs text-slate-500 py-1"
          data-testid="joining-this-week-loading"
        >
          <Loader2 className="w-3 h-3 animate-spin" />
          Checking who's joining this week…
        </div>
      );
    }
    return null;
  }

  const items = data.items || [];
  const inlineNames = items
    .slice(0, 3)
    .map((i) => `${i.candidate_name.split(' ')[0]} (${dayLabel(i.joining_date)})`)
    .join(', ');
  const headlineSuffix =
    items.length > 3 ? `${inlineNames}, +${items.length - 3} more` : inlineNames;

  return (
    <div
      className="rounded-lg border border-sky-200 bg-gradient-to-r from-sky-50 to-white px-3 py-2"
      data-testid="joining-this-week-tile"
    >
      <button
        type="button"
        className="w-full flex items-center gap-2 text-left"
        onClick={() => setExpanded((v) => !v)}
        data-testid="joining-this-week-toggle"
      >
        <Rocket className="w-4 h-4 text-sky-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            <span className="font-semibold text-slate-900">
              {data.count} {data.count === 1 ? 'candidate' : 'candidates'}{' '}
              joining this week
            </span>
            <span className="text-slate-600"> — {headlineSuffix}</span>
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <ul
          className="mt-2 pt-2 border-t border-sky-100 space-y-1.5"
          data-testid="joining-this-week-list"
        >
          {items.map((it) => (
            <li
              key={it.application_id}
              className="flex items-center gap-3 text-xs"
              data-testid={`joining-row-${it.application_id}`}
            >
              <span className="w-14 shrink-0 font-semibold text-sky-700">
                {format(new Date(it.joining_date), 'EEE, dd')}
              </span>
              <span className="flex-1 min-w-0 truncate text-slate-900 font-medium">
                {it.candidate_name}
              </span>
              <span className="text-slate-500 truncate hidden sm:inline">
                {it.job_title}
                {it.client_name ? ` · ${it.client_name}` : ''}
              </span>
              <span className="text-slate-400 shrink-0">
                {it.recruiter_name || ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default JoiningThisWeekTile;
